/* eslint-disable no-undef */
/* eslint-disable consistent-return */
import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useJsApiLoader } from '@react-google-maps/api';
import LocationIcon from '@/assets/icons/location.svg?react';
import PlanSVG from '../planSVG/PlanSVG';
import styles from './LocationAutocomplete.module.scss';

const libraries: 'places'[] = ['places'];

interface LocationAutocompleteProps {
    onPlaceSelect: (place: google.maps.places.PlaceResult) => void;
    placeholder?: string;
    className?: string;
    value?: string;
    onChange?: (value: string) => void;
}

const LocationAutocomplete = ({
    onPlaceSelect,
    placeholder,
    className,
    value,
    onChange,
}: LocationAutocompleteProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const { t } = useTranslation();
    const [error, setError] = useState<string | null>(null);

    const onPlaceSelectRef = useRef(onPlaceSelect);
    const onChangeRef = useRef(onChange);

    useEffect(() => {
        onPlaceSelectRef.current = onPlaceSelect;
        onChangeRef.current = onChange;
    }, [onPlaceSelect, onChange]);

    const { isLoaded, loadError } = useJsApiLoader({
        googleMapsApiKey: import.meta.env.VITE_GMAPS_API_KEY,
        libraries,
    });

    useEffect(() => {
        if (!isLoaded || !containerRef.current) {
            return;
        }

        if (!window.google?.maps?.places?.Autocomplete) {
            console.error('Google Places API not available');
            setError(t('googleMapsBlocked'));
            return;
        }

        let autocomplete: google.maps.places.Autocomplete | null = null;
        let inputElement: HTMLInputElement | null = null;

        try {
            inputElement = document.createElement('input');
            inputElement.type = 'text';
            inputElement.id = 'place-autocomplete-input';
            inputElement.className = styles.input;
            inputElement.placeholder = placeholder || 'Barcelona';
            inputElement.value = value || '';

            if (containerRef.current) {
                containerRef.current.innerHTML = '';
                containerRef.current.appendChild(inputElement);
            }

            autocomplete = new google.maps.places.Autocomplete(inputElement, {
                types: ['(cities)'],
                fields: ['formatted_address', 'geometry', 'name'],
            });

            autocomplete.addListener('place_changed', () => {
                const place = autocomplete?.getPlace();

                if (place && place.geometry && place.geometry.location) {
                    const selectedValue = place.formatted_address || '';

                    if (inputElement) {
                        inputElement.value = selectedValue;
                    }

                    onPlaceSelectRef.current({
                        formatted_address: place.formatted_address || '',
                        geometry: {
                            location: place.geometry.location,
                        },
                        name: place.name || '',
                    });

                    onChangeRef.current?.(selectedValue);
                }
            });

            inputElement.addEventListener('input', (e) => {
                const target = e.target as HTMLInputElement;
                onChangeRef.current?.(target.value);
            });
        } catch (err) {
            console.error('Error initializing Google Places Autocomplete:', err);
            setError(t('googleMapsBlocked'));
        }

        return () => {
            try {
                if (autocomplete) {
                    google.maps.event.clearInstanceListeners(autocomplete);
                }
                if (containerRef.current && inputElement && containerRef.current.contains(inputElement)) {
                    containerRef.current.removeChild(inputElement);
                }
            } catch (cleanupError) {
                console.warn('Cleanup error (safe to ignore):', cleanupError);
            }
            return undefined;
        };
    }, [isLoaded, t, placeholder]);

    useEffect(() => {
        const inputElement = containerRef.current?.querySelector('#place-autocomplete-input') as HTMLInputElement;
        if (inputElement && inputElement.value !== (value || '')) {
            inputElement.value = value || '';
        }
    }, [value]);

    if (loadError) {
        return (
            <div className={`${styles.container} ${className || ''}`}>
                <div style={{ position: 'relative', width: '100%' }}>
                    <PlanSVG svg={<LocationIcon />} className={styles.icon} />
                    <input
                        type="text"
                        placeholder={placeholder || 'Barcelona'}
                        value={value}
                        onChange={(e) => onChange?.(e.target.value)}
                        className={styles.input}
                    />
                </div>
                <div className={styles.errorMessage}>{t('googleMapsBlocked')}</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={`${styles.container} ${className || ''}`}>
                <div style={{ position: 'relative', width: '100%' }}>
                    <PlanSVG svg={<LocationIcon />} className={styles.icon} />
                    <input
                        type="text"
                        placeholder={placeholder || 'Barcelona'}
                        value={value}
                        onChange={(e) => onChange?.(e.target.value)}
                        className={styles.input}
                    />
                </div>
                <div className={styles.errorMessage}>{error}</div>
            </div>
        );
    }

    return (
        <div className={`${styles.container} ${className || ''}`}>
            <div style={{ position: 'relative', width: '100%' }} ref={containerRef}>
                <PlanSVG svg={<LocationIcon />} className={styles.icon} />
            </div>
        </div>
    );
};

export default LocationAutocomplete;
